import React, { useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import AdminDataService from "../services/admin.service";
import { setAuthToken } from "../http-common";
import logger from "../utils/logger";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

const RecetteGlobale = () => {
  const { isAuthenticated, getAccessTokenSilently, user } = useAuth0();

  const email = useMemo(() => (user?.email || "").trim(), [user?.email]);
  const currentYear = useMemo(() => new Date().getFullYear(), []);

  const fmtMoney = useMemo(() => {
    const nf = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 });
    return (v) => {
      const n = Number(v);
      if (!Number.isFinite(n)) return v ?? '';
      return nf.format(n);
    };
  }, []);

  const [authorized, setAuthorized] = useState(null);
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [rows, setRows] = useState([]);

  const [loading, setLoading] = useState(false);
  const [loadingYears, setLoadingYears] = useState(false);

  useEffect(() => {
    const init = async () => {
      if (!isAuthenticated || !email) return;

      try {
        const token = await getAccessTokenSilently({
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          scope: "openid profile email"
        });
        if (token) setAuthToken(token);
      } catch (e) {
        logger.error("Erreur récupération token:", e);
      }

      try {
        const countRes = await AdminDataService.getRecetteAdminCount(email);
        const count = Number(
          countRes?.data?.recette_admin ||
            countRes?.data?.[0]?.recette_admin ||
            countRes?.data?.count ||
            countRes?.data?.[0]?.count ||
            0
        );
        const ok = Number.isFinite(count) && count > 0;
        setAuthorized(ok);
        if (!ok) return;
      } catch (e) {
        logger.error("Erreur vérification habilitation recette:", e);
        setAuthorized(false);
        return;
      }

      setLoadingYears(true);
      try {
        const yRes = await AdminDataService.getRecetteGlobaleYears();
        const ys = Array.isArray(yRes?.data) ? yRes.data : [];
        setYears(ys);

        if (ys.length > 0) {
          setSelectedYear(ys[0]);
        }
      } catch (e) {
        logger.error("Erreur chargement années recette globale:", e);
        setYears([]);
      } finally {
        setLoadingYears(false);
      }
    };

    init();
  }, [currentYear, email, getAccessTokenSilently, isAuthenticated]);

  useEffect(() => {
    const load = async () => {
      if (!isAuthenticated || !email) return;
      if (authorized !== true) return;
      if (!selectedYear) return;

      setLoading(true);
      try {
        const res = await AdminDataService.getRecetteGlobale(selectedYear);
        setRows(Array.isArray(res?.data) ? res.data : []);
      } catch (e) {
        logger.error("Erreur chargement recette globale:", e);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [authorized, email, isAuthenticated, selectedYear]);

  if (authorized === false) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">Accès non autorisé</div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row" style={{ backgroundColor: "#6f42c1", color: "white", padding: "20px" }}>
        <span style={{ fontSize: "1.5rem" }} className="lead">
          <FontAwesomeIcon icon={solid('sack-dollar')} size="2x" className="me-2" /> <b>Recettes globales</b>
        </span>
      </div>
      <div className="px-2 mt-4">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <h4 className="mb-0">Tableau synthèse</h4>
          <div className="d-flex align-items-center gap-2">
          <label className="form-label mb-0">Année</label>
          <select
            className="form-select"
            style={{ width: 140 }}
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            disabled={loadingYears || years.length === 0}
          >
            {years.length === 0 ? (
              <option value={currentYear}>{currentYear}</option>
            ) : (
              years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))
            )}
          </select>
        </div>
      </div>

        <div className="card mt-2 mb-3">
          <div className="card-body py-2 text-center">
            <div className="mt-2 d-flex flex-wrap gap-3 justify-content-center">
              <div className="d-flex align-items-center gap-2">
                <span className="badge rounded-pill bg-primary">N</span>
                <span className="small">Nombre de rendez-vous pris</span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <span className="badge rounded-pill bg-success">M</span>
                <span className="small">Montant généré</span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <span className="badge rounded-pill bg-info text-dark">S</span>
                <span className="small">Part de la structure</span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <span className="badge rounded-pill bg-warning text-dark">O</span>
                <span className="small">Part du promoteur Yemii</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-3">
          {loading ? (
            <div className="alert alert-info">Chargement...</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped table-bordered align-middle">
                <thead>
                  <tr>
                    <th></th>
                    <th className="text-center" colSpan={4}>Trimestre 1</th>
                    <th className="text-center" colSpan={4}>Trimestre 2</th>
                    <th className="text-center" colSpan={4}>Trimestre 3</th>
                    <th className="text-center" colSpan={4}>Trimestre 4</th>
                    <th className="text-center" colSpan={4}>Total annuel</th>
                  </tr>
                  <tr>
                    <th>Structure</th>
                    <th className="text-center">N</th>
                    <th className="text-center">M</th>
                    <th className="text-center">S</th>
                    <th className="text-center">O</th>
                    <th className="text-center">N</th>
                    <th className="text-center">M</th>
                    <th className="text-center">S</th>
                    <th className="text-center">O</th>
                    <th className="text-center">N</th>
                    <th className="text-center">M</th>
                    <th className="text-center">S</th>
                    <th className="text-center">O</th>
                    <th className="text-center">N</th>
                    <th className="text-center">M</th>
                    <th className="text-center">S</th>
                    <th className="text-center">O</th>
                    <th className="text-center">N</th>
                    <th className="text-center">M</th>
                    <th className="text-center">S</th>
                    <th className="text-center">O</th>
                  </tr>
                </thead>
                <tbody>
                  {(rows || []).map((r, idx) => (
                    <tr key={`${r.structure}-${idx}`} className="text-end">
                      <td className="text-start">{r.structure}</td>
                      <td>{r.t1_nb}</td>
                      <td>{fmtMoney(r.t1)}</td>
                      <td>{fmtMoney(r.t1_str)}</td>
                      <td>{fmtMoney(r.t1_obr)}</td>
                      <td>{r.t2_nb}</td>
                      <td>{fmtMoney(r.t2)}</td>
                      <td>{fmtMoney(r.t2_str)}</td>
                      <td>{fmtMoney(r.t2_obr)}</td>
                      <td>{r.t3_nb}</td>
                      <td>{fmtMoney(r.t3)}</td>
                      <td>{fmtMoney(r.t3_str)}</td>
                      <td>{fmtMoney(r.t3_obr)}</td>
                      <td>{r.t4_nb}</td>
                      <td>{fmtMoney(r.t4)}</td>
                      <td>{fmtMoney(r.t4_str)}</td>
                      <td>{fmtMoney(r.t4_obr)}</td>
                      <td>{r.total_nb}</td>
                      <td>{fmtMoney(r.total_an)}</td>
                      <td>{fmtMoney(r.total_an_str)}</td>
                      <td>{fmtMoney(r.total_an_obr)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecetteGlobale;
